import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Bot, HeartPulse, Stethoscope, Pill, Syringe, Settings, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://www.modcarv.updatconsulting.app/api'
  : 'http://localhost:8000/api';
// const BASE_URL = 'http://localhost:8000/api';

const PublicChatbot = () => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showPersonalizationForm, setShowPersonalizationForm] = useState(false);
  const [infoForm, setInfoForm] = useState({
    age: '',
    sexe: '',
    antecedents: '',
    poids: ''
  });

  const chatWindowRef = useRef(null);
  const hasWelcomed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    const welcomeMessage = {
      id: 'welcome',
      sender: 'bot',
      text: "Bienvenue dans votre Assistant Cardiovasculaire !\n\nJe suis spécialisé dans les maladies cardiovasculaires au Sénégal.\n\n**Que puis-je faire pour vous aider aujourd'hui ?**",
      sources: [],
      dateCreation: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
    hasWelcomed.current = true;
  }, []);

  useEffect(() => {
    chatWindowRef.current?.scrollTo({
      top: chatWindowRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages, isLoading]);

  const askQuestion = async (question) => {
    try {
      const response = await fetch(`${BASE_URL}/ask/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          question, 
          session_id: sessionId 
        })
      });

      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      const data = await response.json();

      return {
        answer: data.answer || "Désolé, je n'ai pas compris votre question.",
        sources: data.sources || [],
        session_id: data.session_id || sessionId,
         confidence_score: data.confidence_score || 0
      };
    } catch (error) {
      console.error('Erreur API:', error);
      return { 
        answer: " Erreur de connexion au serveur. Veuillez réessayer dans quelques instants.", 
        sources: [],
        confidence_score: 0
      };
    }
  };

  const handleSend = async () => {
    if (!question.trim()) return;

    const userMessage = { 
      id: Date.now(), 
      sender: 'user', 
      text: question.trim(),
      dateCreation: new Date().toISOString()
    };
    setMessages((prev) => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    const { answer, sources, session_id, confidence_score } = await askQuestion(userMessage.text);
    
    if (session_id && session_id !== sessionId) {
      setSessionId(session_id);
    }

    const botMessage = { 
      id: Date.now() + 1, 
      sender: 'bot', 
      text: answer, 
      sources,
      confidence_score,
      dateCreation: new Date().toISOString()
    };
    setMessages((prev) => [...prev, botMessage]);
    setIsLoading(false);
  };

  const handleSubmitInfo = async () => {
    const { age, sexe, antecedents, poids } = infoForm;
    
    if (!sessionId) {
      alert(" Erreur de session. Veuillez recharger la page.");
      return;
    }

    if (!age || !sexe || !antecedents || !poids) {
      alert(" Veuillez remplir tous les champs pour une personnalisation optimale.");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/ask_info/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_id: sessionId, 
          age, 
          sexe, 
          antecedents, 
          poids 
        })
      });

      if (response.ok) {
        setMessages((prev) => [
          ...prev,
          { 
            id: Date.now(),
            sender: 'bot', 
            text: " Informations enregistrées avec succès !\n\nJe vais maintenant personnaliser mes réponses en tenant compte de :\n- Votre âge : " + age + " ans\n- Votre sexe : " + sexe + "\n- Vos antécédents : " + antecedents + "\n- Votre poids : " + poids + " kg\n\nVous pouvez maintenant me poser vos questions !",
            dateCreation: new Date().toISOString()
          }
        ]);
        setShowPersonalizationForm(false);
        setInfoForm({ age: '', sexe: '', antecedents: '', poids: '' });
      } else {
        throw new Error('Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      console.error("Erreur envoi info:", err);
      alert(" Erreur lors de l'enregistrement. Veuillez réessayer.");
    }
  };

  const clearConversation = async () => {
    if (!window.confirm(" Voulez-vous vraiment effacer toute la conversation ?")) return;
    
    try {
      if (sessionId) {
        await fetch(`${BASE_URL}/sessions/${sessionId}`, {
          method: 'DELETE',
        });
      }
      
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      setMessages([]);
      hasWelcomed.current = false;
      
      setTimeout(() => {
        const welcomeMessage = {
          id: 'welcome_new',
          sender: 'bot',
          text: " **Conversation réinitialisée !**\n\nJe suis à nouveau prêt à répondre à vos questions sur les maladies cardiovasculaires.\n\nQue puis-je faire pour vous ?",
          sources: [],
          dateCreation: new Date().toISOString()
        };
        setMessages([welcomeMessage]);
        hasWelcomed.current = true;
      }, 100);
      
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.7) return 'text-green-600';
    if (score >= 0.4) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="relative flex flex-col h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 mb-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg z-20 max-w-7xl mx-auto w-full border border-white/20">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
        >
          <Home size={18} /> Accueil
        </button>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-indigo-700 font-semibold">
            <HeartPulse size={20} className="text-red-500" />
            <span className="hidden sm:inline">Assistant Cardiovasculaire</span>
            <span className="sm:hidden">Cardio AI</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPersonalizationForm(!showPersonalizationForm)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <Settings size={16} /> 
            <span className="hidden sm:inline">Personnaliser</span>
          </button>
        </div>
      </div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Bot className="absolute top-10 left-5 text-indigo-200 opacity-20 w-32 h-32 animate-spin-slow" />
        <HeartPulse className="absolute top-32 right-1/4 text-red-200 opacity-30 w-28 h-28 animate-pulse" />
        <Stethoscope className="absolute bottom-32 left-1/4 text-blue-200 opacity-20 w-24 h-24" />
        <Pill className="absolute top-20 right-10 text-pink-200 opacity-20 w-20 h-20 animate-bounce" />
        <Syringe className="absolute bottom-10 right-1/5 text-green-200 opacity-20 w-24 h-24" />
      </div>

      <style jsx>{`
        .animate-spin-slow {
          animation: spin 15s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {showPersonalizationForm && (
        <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-lg mb-4 z-10 border border-white/30">
          <h3 className="font-semibold text-indigo-700 mb-4 text-center">
             Personnalisez vos réponses médicales
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <input
              type="number"
              placeholder="Âge (ex: 45)"
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={infoForm.age}
              onChange={(e) => setInfoForm({ ...infoForm, age: e.target.value })}
            />
            <select
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={infoForm.sexe}
              onChange={(e) => setInfoForm({ ...infoForm, sexe: e.target.value })}
            >
              <option value="">Sélectionner le sexe</option>
              <option value="Homme">Homme</option>
              <option value="Femme">Femme</option>
            </select>
            <input
              type="text"
              placeholder="Antécédents (ex: diabète, hypertension)"
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:col-span-1"
              value={infoForm.antecedents}
              onChange={(e) => setInfoForm({ ...infoForm, antecedents: e.target.value })}
            />
            <input
              type="number"
              placeholder="Poids en kg (ex: 70)"
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={infoForm.poids}
              onChange={(e) => setInfoForm({ ...infoForm, poids: e.target.value })}
            />
          </div>
          <div className="flex justify-center gap-3 mt-4">
            <button
              onClick={handleSubmitInfo}
              className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
               Enregistrer
            </button>
            <button
              onClick={() => setShowPersonalizationForm(false)}
              className="bg-gray-300 text-gray-700 py-2 px-6 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <div
        ref={chatWindowRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 z-10 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl mb-4"
      >
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-10">
            <Bot size={48} className="mx-auto mb-4 text-indigo-300" />
            <p>Chargement de votre assistant cardiovasculaire...</p>
          </div>
        )}

        {messages.map((msg, i) => {
          const isUser = msg.sender === 'user';
          const date = msg.dateCreation ? new Date(msg.dateCreation).toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : '';
          
          return (
            <div
              key={i}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`relative px-4 py-3 rounded-2xl text-sm break-words shadow-lg ${
                  isUser 
                    ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white max-w-xs sm:max-w-md' 
                    : 'bg-white/95 backdrop-blur-sm text-gray-800 max-w-xs sm:max-w-2xl border border-white/30'
                }`}
              >
                {!isUser && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      </div>
                      
                      {msg.confidence_score !== undefined && msg.confidence_score > 0 && (
                        <div className={`text-xs mt-2 ${getConfidenceColor(msg.confidence_score)}`}>
                           {/* Confiance: {Math.round(msg.confidence_score * 100)}% */}
                        </div>
                      )}
                      
                      {msg.sources?.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-200">
                          <div className="text-xs text-blue-700 font-semibold mb-1"> Sources médicales :</div>
                          <div className="space-y-1">
                            {msg.sources.slice(0, 3).map((src, idx) => {
                              const rawUrl = src?.url || src?.metadata?.url;
                              if (!rawUrl) return null;

                              let hostname = rawUrl;
                              try {
                                hostname = new URL(rawUrl).hostname;
                              } catch (error) {
                              }

                              return (
                                <div key={idx} className="text-xs">
                                  <a
                                    href={rawUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline break-all"
                                  >
                                    🔗 {hostname}
                                  </a>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {date && (
                        <div className="text-[10px] mt-2 text-gray-500">{date}</div>
                      )}
                    </div>
                  </div>
                )}
                
                {isUser && (
                  <>
                    {msg.text}
                    {date && (
                      <div className="text-[10px] mt-1 text-blue-100">{date}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white/95 backdrop-blur-sm px-4 py-3 rounded-2xl shadow-lg border border-white/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-full flex items-center justify-center">
                  <Bot size={16} className="text-white animate-pulse" />
                </div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 z-10 bg-white/90 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-white/30">
        <div className="flex-1 flex items-center gap-2">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder=" Posez votre question ici... (Ex: Comment traiter l'hypertension ?)"
            className="flex-1 p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            rows={2}
            disabled={isLoading}
          />
          <div className="flex flex-col gap-2">
            <button
              onClick={handleSend}
              className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-indigo-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-lg"
              disabled={isLoading || !question.trim()}
            >
              {isLoading ? '⏳' : '➤'}
            </button>
            <button
              onClick={clearConversation}
              className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200 transition-all text-sm"
              title="Effacer la conversation"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>

      <div className="text-center mt-2 text-xs text-gray-600 z-10">
        <p> Assistant médical spécialisé - Maladies cardiovasculaires au Sénégal</p>
        <p> En cas d'urgence: 15 (urgences) ou 1515 (SAMU Saint-Louis)</p>
      </div>
    </div>
  );
};

export default PublicChatbot;